import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ProductMatchAutocomplete from '@/components/ProductMatchAutocomplete';
import { X, Plus } from 'lucide-react';

export const EditOrderDialog = ({
  showEditOrder,
  setShowEditOrder,
  editOrderSubtotal,
  editOrderTotal,
  handleSaveEditOrder,
  phases,
  formatCurrency,
}) => (
  <Dialog open={!!showEditOrder} onOpenChange={() => setShowEditOrder(null)}>
    <DialogContent className="sm:max-w-3xl border border-zinc-200 rounded-none p-0 max-h-[90vh] overflow-hidden flex flex-col">
      {showEditOrder && (
        <>
          <DialogHeader className="p-5 pb-0 flex-shrink-0">
            <DialogTitle className="text-xl font-heading font-bold uppercase tracking-wide">Edit Order</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveEditOrder} className="flex flex-col flex-1 overflow-hidden">
            <div className="p-5 pt-3 space-y-4 overflow-y-auto flex-1">
              {/* Row 1 */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Vendor Name</label>
                  <input type="text" value={showEditOrder.vendor_name}
                    onChange={e => setShowEditOrder({...showEditOrder, vendor_name: e.target.value})}
                    data-testid="edit-vendor-name"
                    className="w-full h-10 px-3 border-2 border-zinc-300 text-sm focus:border-primary focus:outline-none" required />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">PO Number</label>
                  <input type="text" value={showEditOrder.po_number}
                    onChange={e => setShowEditOrder({...showEditOrder, po_number: e.target.value})}
                    data-testid="edit-po-number"
                    className="w-full h-10 px-3 border-2 border-zinc-300 text-sm font-mono focus:border-primary focus:outline-none" />
                </div>
              </div>
              {/* Row 2 */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Phase</label>
                  <select value={showEditOrder.phase_id}
                    onChange={e => setShowEditOrder({...showEditOrder, phase_id: e.target.value})}
                    data-testid="edit-phase"
                    className="w-full h-10 px-3 border-2 border-zinc-300 text-sm focus:border-primary focus:outline-none">
                    <option value="">No Phase</option>
                    {phases.map(p => <option key={p.phase_id} value={p.phase_id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Order Date</label>
                  <input type="date" value={showEditOrder.order_date}
                    onChange={e => setShowEditOrder({...showEditOrder, order_date: e.target.value})}
                    data-testid="edit-order-date"
                    className="w-full h-10 px-3 border-2 border-zinc-300 text-sm focus:border-primary focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Expected Delivery</label>
                  <input type="date" value={showEditOrder.expected_delivery_date}
                    onChange={e => setShowEditOrder({...showEditOrder, expected_delivery_date: e.target.value})}
                    data-testid="edit-expected-delivery"
                    className="w-full h-10 px-3 border-2 border-zinc-300 text-sm focus:border-primary focus:outline-none" />
                </div>
              </div>
              {/* Status + Description */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Status</label>
                  <select value={showEditOrder.status}
                    onChange={e => setShowEditOrder({...showEditOrder, status: e.target.value})}
                    data-testid="edit-status"
                    className="w-full h-10 px-3 border-2 border-zinc-300 text-sm font-bold uppercase focus:border-primary focus:outline-none">
                    {['Draft','Ordered','Confirmed','Shipped','Delivered','Issue'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Description</label>
                  <input type="text" value={showEditOrder.description}
                    onChange={e => setShowEditOrder({...showEditOrder, description: e.target.value})}
                    data-testid="edit-description"
                    className="w-full h-10 px-3 border-2 border-zinc-300 text-sm focus:border-primary focus:outline-none" />
                </div>
              </div>

              {/* Line Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold uppercase text-zinc-500">Line Items</label>
                  <button type="button"
                    onClick={() => setShowEditOrder({...showEditOrder, line_items: [...showEditOrder.line_items, { _key: `li-edit-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, product_description: '', quantity: 1, unit: 'each', unit_price: 0 }]})}
                    data-testid="edit-add-line-item"
                    className="h-7 px-3 text-[10px] font-bold uppercase bg-zinc-900 text-white hover:bg-zinc-800 flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Add Item
                  </button>
                </div>
                <div className="border-2 border-zinc-300">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-100 border-b-2 border-zinc-900">
                      <tr>
                        <th className="text-left p-2 text-xs font-bold uppercase">Product</th>
                        <th className="text-right p-2 text-xs font-bold uppercase w-20">Qty</th>
                        <th className="text-center p-2 text-xs font-bold uppercase w-20">Unit</th>
                        <th className="text-right p-2 text-xs font-bold uppercase w-28">Price</th>
                        <th className="text-right p-2 text-xs font-bold uppercase w-28">Extended</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {showEditOrder.line_items.map((li, idx) => {
                        const ext = (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0);
                        const rowKey = li.line_item_id || li._key || `edit-li-${idx}`;
                        return (
                          <tr key={rowKey} className="border-b border-zinc-200 last:border-0" data-testid={`edit-line-item-${idx}`}>
                            <td className="p-1.5">
                              <ProductMatchAutocomplete
                                value={li.product_description}
                                onChange={(v) => {
                                  const items = [...showEditOrder.line_items];
                                  items[idx] = { ...items[idx], product_description: v };
                                  if (items[idx].product_id) {
                                    items[idx].product_id = null;
                                    items[idx].manufacturer_sku = null;
                                    items[idx].match_confidence = null;
                                  }
                                  setShowEditOrder({...showEditOrder, line_items: items});
                                }}
                                onProductSelect={(sel) => {
                                  const items = [...showEditOrder.line_items];
                                  items[idx] = {
                                    ...items[idx],
                                    product_description: sel.product_description,
                                    product_id: sel.product_id,
                                    manufacturer_sku: sel.manufacturer_sku,
                                    match_confidence: sel.match_confidence,
                                    unit: sel.unit_of_measure || items[idx].unit,
                                  };
                                  setShowEditOrder({...showEditOrder, line_items: items});
                                }}
                                currentProductId={li.product_id}
                                currentMatchConfidence={li.match_confidence}
                                currentMatchedName={li.product_description}
                                placeholder="Description"
                                testIdPrefix={`edit-line-${idx}-product`}
                                inputClassName="w-full h-8 px-2 border border-zinc-200 text-sm focus:border-primary focus:outline-none"
                              />
                            </td>
                            <td className="p-1.5">
                              <input type="number" value={li.quantity} min="0" step="any"
                                onChange={e => {
                                  const items = [...showEditOrder.line_items];
                                  items[idx] = {...items[idx], quantity: e.target.value};
                                  setShowEditOrder({...showEditOrder, line_items: items});
                                }}
                                data-testid={`edit-li-qty-${idx}`}
                                className="w-full h-8 px-2 border border-zinc-200 text-sm text-right font-mono focus:border-primary focus:outline-none" />
                            </td>
                            <td className="p-1.5">
                              <select value={li.unit}
                                onChange={e => {
                                  const items = [...showEditOrder.line_items];
                                  items[idx] = {...items[idx], unit: e.target.value};
                                  setShowEditOrder({...showEditOrder, line_items: items});
                                }}
                                className="w-full h-8 px-1 border border-zinc-200 text-xs focus:border-primary focus:outline-none">
                                {['each','ft','lf','box','roll','pallet','lb','gal','set','lot'].map(u => <option key={u} value={u}>{u}</option>)}
                              </select>
                            </td>
                            <td className="p-1.5">
                              <input type="number" value={li.unit_price} min="0" step="0.01"
                                onChange={e => {
                                  const items = [...showEditOrder.line_items];
                                  items[idx] = {...items[idx], unit_price: e.target.value};
                                  setShowEditOrder({...showEditOrder, line_items: items});
                                }}
                                data-testid={`edit-li-price-${idx}`}
                                className="w-full h-8 px-2 border border-zinc-200 text-sm text-right font-mono focus:border-primary focus:outline-none" />
                            </td>
                            <td className="p-1.5 text-right font-mono font-bold text-sm">{formatCurrency(ext)}</td>
                            <td className="p-1.5">
                              {showEditOrder.line_items.length > 1 && (
                                <button type="button"
                                  onClick={() => {
                                    const items = showEditOrder.line_items.filter((_, i) => i !== idx);
                                    setShowEditOrder({...showEditOrder, line_items: items});
                                  }}
                                  data-testid={`edit-li-remove-${idx}`}
                                  className="w-7 h-7 flex items-center justify-center text-zinc-400 hover:text-red-600">
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Tax, Freight, Totals */}
              <div className="bg-zinc-50 border-2 border-zinc-300 p-4">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Tax ($)</label>
                    <input type="number" value={showEditOrder.tax} min="0" step="0.01"
                      onChange={e => setShowEditOrder({...showEditOrder, tax: e.target.value})}
                      data-testid="edit-tax"
                      className="w-full h-10 px-3 border-2 border-zinc-300 text-sm font-mono focus:border-primary focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Freight ($)</label>
                    <input type="number" value={showEditOrder.freight} min="0" step="0.01"
                      onChange={e => setShowEditOrder({...showEditOrder, freight: e.target.value})}
                      data-testid="edit-freight"
                      className="w-full h-10 px-3 border-2 border-zinc-300 text-sm font-mono focus:border-primary focus:outline-none" />
                  </div>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Subtotal:</span>
                  <span className="font-mono font-bold">{formatCurrency(editOrderSubtotal)}</span>
                </div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Tax:</span>
                  <span className="font-mono font-bold">{formatCurrency(parseFloat(showEditOrder.tax) || 0)}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span>Freight:</span>
                  <span className="font-mono font-bold">{formatCurrency(parseFloat(showEditOrder.freight) || 0)}</span>
                </div>
                <div className="flex justify-between text-xl border-t-2 border-zinc-900 pt-2">
                  <span className="font-heading font-bold uppercase">TOTAL:</span>
                  <span className="font-heading font-bold uppercase" data-testid="edit-total">{formatCurrency(editOrderTotal)}</span>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-bold uppercase text-zinc-500 mb-1">Notes</label>
                <textarea value={showEditOrder.notes} rows={2}
                  onChange={e => setShowEditOrder({...showEditOrder, notes: e.target.value})}
                  data-testid="edit-notes"
                  className="w-full px-3 py-2 border-2 border-zinc-300 text-sm focus:border-primary focus:outline-none resize-none" />
              </div>
            </div>

            {/* Footer */}
            <div className="p-5 pt-0 flex gap-3 flex-shrink-0 border-t-2 border-zinc-900 mt-2">
              <button type="submit" data-testid="save-edit-order-btn"
                className="flex-1 h-11 bg-primary text-zinc-900 font-bold uppercase tracking-wide border-2 border-zinc-900 hover:bg-primary-hover active:translate-y-0.5 transition-all">
                Save Changes
              </button>
              <button type="button" onClick={() => setShowEditOrder(null)}
                className="h-11 px-6 bg-white text-zinc-900 font-bold uppercase border-2 border-zinc-300">
                Cancel
              </button>
            </div>
          </form>
        </>
      )}
    </DialogContent>
  </Dialog>
);
